import React, { useState } from 'react';

export const GiftCodeSection = ({ childId, onRedeemSuccess, t }) => {
  const [showGiftCode, setShowGiftCode] = useState(false);
  const [giftCode, setGiftCode] = useState('');
  const [giftCodeError, setGiftCodeError] = useState('');
  const [giftCodeInfo, setGiftCodeInfo] = useState(null);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [isRedeemingCode, setIsRedeemingCode] = useState(false);

  const handleGiftCodeChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setGiftCode(value);
    setGiftCodeInfo(null);
    setGiftCodeError('');
  };

  const handleValidateGiftCode = async () => {
    if (giftCode.length < 4) {
      setGiftCodeError(t('gift_codes.errors.code_too_short') || 'Code must be at least 4 characters');
      return;
    }

    setIsValidatingCode(true);
    setGiftCodeError('');

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const response = await fetch('/api/guardians/gift_codes/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ code: giftCode, child_id: childId })
      });

      const data = await response.json();

      if (data.valid) {
        setGiftCodeInfo(data.gift_code);
        setGiftCodeError('');
      } else {
        setGiftCodeError(data.error);
        setGiftCodeInfo(null);
      }
    } catch (error) {
      setGiftCodeError(t('gift_codes.errors.network_error') || 'Network error, please try again');
      setGiftCodeInfo(null);
    } finally {
      setIsValidatingCode(false);
    }
  };

  const handleRedeemGiftCode = async () => {
    setIsRedeemingCode(true);
    setGiftCodeError('');

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const response = await fetch('/api/guardians/gift_codes/redeem', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ code: giftCode, child_id: childId })
      });

      const data = await response.json();

      if (data.success) {
        onRedeemSuccess();
      } else {
        setGiftCodeError(data.error?.message || t('gift_codes.errors.network_error') || 'Failed to redeem code');
      }
    } catch (error) {
      setGiftCodeError(t('gift_codes.errors.network_error') || 'Network error, please try again');
    } finally {
      setIsRedeemingCode(false);
    }
  };

  const handleClose = () => {
    setShowGiftCode(false);
    setGiftCode('');
    setGiftCodeError('');
    setGiftCodeInfo(null);
  };

  if (!showGiftCode) {
    return (
      <button
        type="button"
        className="btn btn-link text-decoration-none"
        onClick={() => setShowGiftCode(true)}
        data-testid="gift-code-toggle-link"
      >
        <i className="bi bi-gift me-2"></i>
        {t('gift_codes.have_gift_code') || 'Have a gift code?'}
      </button>
    );
  }

  return (
    <div className="card p-3" style={{ background: '#f8f9fa' }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center">
          <i className="bi bi-gift text-primary me-2"></i>
          <span className="fw-semibold">{t('gift_codes.enter_code') || 'Enter gift code'}</span>
        </div>
        <button type="button" className="btn-close btn-close-sm" onClick={handleClose}></button>
      </div>
      <div className="input-group mb-2">
        <input
          type="text"
          className={`form-control ${giftCodeError ? 'is-invalid' : ''} ${giftCodeInfo ? 'is-valid' : ''}`}
          placeholder="XXXX"
          value={giftCode}
          onChange={handleGiftCodeChange}
          maxLength={20}
          data-testid="gift-code-input"
          style={{ textTransform: 'uppercase' }}
        />
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handleValidateGiftCode}
          disabled={giftCode.length < 4 || isValidatingCode}
          data-testid="gift-code-validate-button"
        >
          {isValidatingCode ? (
            <span className="spinner-border spinner-border-sm"></span>
          ) : (
            t('gift_codes.validate') || 'Validate'
          )}
        </button>
      </div>
      {giftCodeError && (
        <div className="text-danger small mb-2" data-testid="gift-code-error">
          {giftCodeError}
        </div>
      )}
      {giftCodeInfo && (
        <div className="alert alert-success py-2 mb-2" data-testid="gift-code-info">
          <div className="small">
            <strong>{giftCodeInfo.subscription_type === 'premium' ? 'Premium' : 'Pro'}</strong>
            {' - '}
            {giftCodeInfo.duration_label}
          </div>
        </div>
      )}
      {giftCodeInfo && (
        <button
          type="button"
          className="btn btn-primary w-100"
          onClick={handleRedeemGiftCode}
          disabled={isRedeemingCode}
          data-testid="gift-code-redeem-button"
        >
          {isRedeemingCode ? (
            <span className="spinner-border spinner-border-sm me-2"></span>
          ) : (
            <i className="bi bi-check-lg me-2"></i>
          )}
          {t('gift_codes.redeem') || 'Redeem Code'}
        </button>
      )}
    </div>
  );
};
